/**
 * Song Metadata Normalization and Validation Utilities
 *
 * Implements sophisticated field normalization patterns inspired by Stars Layer
 * for consistent song metadata handling across the application.
 */

/**
 * Genre normalization with comprehensive mapping
 */
const GENRE_MAPPINGS: Record<string, string> = {
  // Primary genres (standardized)
  pop: 'Pop',
  rock: 'Rock',
  'hip-hop': 'Hip-Hop',
  hiphop: 'Hip-Hop',
  'hip hop': 'Hip-Hop',
  rap: 'Hip-Hop',
  'r&b': 'R&B',
  rnb: 'R&B',
  soul: 'R&B',
  electronic: 'Electronic',
  edm: 'Electronic',
  dance: 'Electronic',
  house: 'Electronic',
  techno: 'Electronic',
  country: 'Country',
  folk: 'Folk',
  indie: 'Indie',
  alternative: 'Alternative',
  jazz: 'Jazz',
  classical: 'Classical',
  blues: 'Blues',
  reggae: 'Reggae',
  latin: 'Latin',
  world: 'World',
  ambient: 'Ambient',
  experimental: 'Experimental',
};

/**
 * Mood normalization with emotional mapping
 */
const MOOD_MAPPINGS: Record<string, string> = {
  // Positive moods
  happy: 'Happy',
  upbeat: 'Happy',
  cheerful: 'Happy',
  joyful: 'Happy',
  energetic: 'Energetic',
  excited: 'Energetic',
  dynamic: 'Energetic',
  powerful: 'Energetic',
  calm: 'Calm',
  peaceful: 'Calm',
  relaxed: 'Calm',
  chill: 'Calm',
  romantic: 'Romantic',
  loving: 'Romantic',
  passionate: 'Romantic',
  sensual: 'Romantic',

  // Neutral/Complex moods
  melancholy: 'Melancholic',
  melancholic: 'Melancholic',
  sad: 'Melancholic',
  somber: 'Melancholic',
  mysterious: 'Mysterious',
  dark: 'Dark',
  moody: 'Dark',
  intense: 'Intense',
  aggressive: 'Aggressive',
  angry: 'Aggressive',
  nostalgic: 'Nostalgic',
  dreamy: 'Dreamy',
  ethereal: 'Dreamy',
  atmospheric: 'Atmospheric',
};

/**
 * Energy level normalization
 */
const ENERGY_MAPPINGS: Record<string, string> = {
  low: 'Low',
  mellow: 'Low',
  quiet: 'Low',
  subdued: 'Low',
  medium: 'Medium',
  moderate: 'Medium',
  balanced: 'Medium',
  high: 'High',
  intense: 'High',
  powerful: 'High',
  explosive: 'High',
};

/**
 * Tempo normalization
 */
const TEMPO_MAPPINGS: Record<string, string> = {
  slow: 'Slow',
  ballad: 'Slow',
  'laid-back': 'Slow',
  medium: 'Medium',
  moderate: 'Medium',
  'mid-tempo': 'Medium',
  fast: 'Fast',
  'up-tempo': 'Fast',
  rapid: 'Fast',
  quick: 'Fast',
};

/**
 * Vocal type normalization
 */
const VOCAL_TYPE_MAPPINGS: Record<string, string> = {
  soprano: 'Soprano',
  alto: 'Alto',
  tenor: 'Tenor',
  bass: 'Bass',
  baritone: 'Baritone',
  'mezzo-soprano': 'Mezzo-Soprano',
  countertenor: 'Countertenor',
  // Simplified terms
  high: 'Soprano',
  low: 'Bass',
  medium: 'Alto',
  mid: 'Alto',
};

/**
 * Age appropriateness normalization
 */
const AGE_APPROPRIATENESS_MAPPINGS: Record<string, string> = {
  'all-ages': 'all ages',
  all_ages: 'all ages',
  everyone: 'all ages',
  'family-friendly': 'all ages',
  'kid-friendly': 'all ages',
  children: 'all ages',
  teen: 'teen+',
  teenager: 'teen+',
  teens: 'teen+',
  'young-adult': 'teen+',
  adult: 'mature',
  mature: 'mature',
  '18+': 'mature',
  explicit: 'explicit',
  'parental-advisory': 'explicit',
};

/**
 * Normalize genre field with fallback
 */
export function normalizeGenre(
  rawGenre: string | string[] | undefined | null
): string[] {
  // Handle array input (backend now sends arrays)
  if (Array.isArray(rawGenre)) {
    return rawGenre
      .map(g => {
        if (!g || typeof g !== 'string') return '';
        const cleaned = g.toLowerCase().trim();
        const normalized = GENRE_MAPPINGS[cleaned];
        return (
          normalized ||
          g
            .split(' ')
            .map(
              word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join(' ')
        );
      })
      .filter(g => g !== '');
  }

  // Handle legacy string input (for backward compatibility)
  if (!rawGenre || typeof rawGenre !== 'string') return [];

  const cleaned = rawGenre.toLowerCase().trim();
  const normalized = GENRE_MAPPINGS[cleaned];

  if (normalized) {
    return [normalized];
  }

  // Return title case for unknown genres
  return [
    rawGenre
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' '),
  ];
}

/**
 * Normalize mood field with emotional intelligence
 */
export function normalizeMood(
  rawMood: string | string[] | undefined | null
): string[] {
  // Handle array input (backend now sends arrays)
  if (Array.isArray(rawMood)) {
    return rawMood
      .map(m => {
        if (!m || typeof m !== 'string') return '';
        const cleaned = m.toLowerCase().trim();
        const normalized = MOOD_MAPPINGS[cleaned];
        return (
          normalized || m.charAt(0).toUpperCase() + m.slice(1).toLowerCase()
        );
      })
      .filter(m => m !== '');
  }

  // Handle legacy string input (for backward compatibility)
  if (!rawMood || typeof rawMood !== 'string') return [];

  const cleaned = rawMood.toLowerCase().trim();
  const normalized = MOOD_MAPPINGS[cleaned];

  if (normalized) {
    return [normalized];
  }

  // Return title case for unknown moods
  return [rawMood.charAt(0).toUpperCase() + rawMood.slice(1).toLowerCase()];
}

/**
 * Normalize energy level
 */
export function normalizeEnergy(rawEnergy: string | undefined | null): string {
  if (!rawEnergy || typeof rawEnergy !== 'string') return '';

  const cleaned = rawEnergy.toLowerCase().trim();
  const normalized = ENERGY_MAPPINGS[cleaned];

  if (normalized) {
    return normalized;
  }

  // Default to Medium if unrecognized
  return 'Medium';
}

/**
 * Normalize tempo
 */
export function normalizeTempo(rawTempo: string | undefined | null): string {
  if (!rawTempo || typeof rawTempo !== 'string') return '';

  const cleaned = rawTempo.toLowerCase().trim();
  const normalized = TEMPO_MAPPINGS[cleaned];

  if (normalized) {
    return normalized;
  }

  // Return title case for unknown tempos
  return rawTempo.charAt(0).toUpperCase() + rawTempo.slice(1).toLowerCase();
}

/**
 * Normalize vocal type/range
 */
export function normalizeVocalType(
  rawVocalType: string | undefined | null
): string {
  if (!rawVocalType || typeof rawVocalType !== 'string') return '';

  const cleaned = rawVocalType.toLowerCase().trim();
  const normalized = VOCAL_TYPE_MAPPINGS[cleaned];

  if (normalized) {
    return normalized;
  }

  // Return title case for unknown vocal types
  return rawVocalType
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Normalize and deduplicate age appropriateness array
 */
export function normalizeAgeAppropriateness(
  rawAgeAppropriateness: any
): string[] {
  if (!rawAgeAppropriateness) return [];

  // Handle single string
  if (typeof rawAgeAppropriateness === 'string') {
    const normalized =
      AGE_APPROPRIATENESS_MAPPINGS[rawAgeAppropriateness.toLowerCase().trim()];
    return normalized ? [normalized] : [rawAgeAppropriateness];
  }

  // Handle array
  if (Array.isArray(rawAgeAppropriateness)) {
    const normalized = rawAgeAppropriateness
      .filter((age: any) => typeof age === 'string' && age.length > 1)
      .map((age: string) => {
        const cleaned = age.toLowerCase().trim();
        return AGE_APPROPRIATENESS_MAPPINGS[cleaned] || age;
      });

    // Remove duplicates
    return Array.from(new Set(normalized));
  }

  return [];
}

/**
 * Normalize BPM to reasonable range
 */
export function normalizeBPM(rawBPM: any): number | null {
  if (rawBPM === null || rawBPM === undefined) return null;

  const bpm =
    typeof rawBPM === 'string' ? parseInt(rawBPM, 10) : Number(rawBPM);

  if (isNaN(bpm)) return null;

  // Reasonable BPM range for music (40-200)
  if (bpm < 40) return 40;
  if (bpm > 200) return 200;

  return bpm;
}

/**
 * Normalize duration to seconds
 */
export function normalizeDuration(rawDuration: any): number | null {
  if (rawDuration === null || rawDuration === undefined) return null;

  const duration =
    typeof rawDuration === 'string'
      ? parseInt(rawDuration, 10)
      : Number(rawDuration);

  if (isNaN(duration)) return null;

  // Reasonable duration range (15 seconds to 30 minutes)
  if (duration < 15) return 15;
  if (duration > 1800) return 1800; // 30 minutes

  return duration;
}

/**
 * Normalize musical key
 */
export function normalizeKey(rawKey: string | undefined | null): string {
  if (!rawKey || typeof rawKey !== 'string') return '';

  const cleaned = rawKey.trim();

  // Basic key format validation and normalization
  const keyPattern = /^([A-G])([#b]?)(\s+(major|minor|maj|min))?$/i;
  const match = cleaned.match(keyPattern);

  if (match) {
    const [, note, accidental = '', , mode = 'major'] = match;
    const normalizedMode =
      mode.toLowerCase() === 'min' || mode.toLowerCase() === 'minor'
        ? 'minor'
        : 'major';
    return `${note.toUpperCase()}${accidental} ${normalizedMode}`;
  }

  // Return as-is if doesn't match pattern
  return cleaned;
}

/**
 * Comprehensive song metadata normalization
 */
export function normalizeSongMetadata(rawMetadata: any): any {
  if (!rawMetadata || typeof rawMetadata !== 'object') return rawMetadata;

  return {
    ...rawMetadata,
    genre: normalizeGenre(rawMetadata.genre),
    mood: normalizeMood(rawMetadata.mood),
    energy: normalizeEnergy(rawMetadata.energy),
    tempo: normalizeTempo(rawMetadata.tempo),
    vocalType: normalizeVocalType(
      rawMetadata.vocalType || rawMetadata.vocalRange
    ),
    ageAppropriateness: normalizeAgeAppropriateness(
      rawMetadata.ageAppropriateness
    ),
    bpm: normalizeBPM(rawMetadata.bpm),
    duration: normalizeDuration(rawMetadata.duration),
    key: normalizeKey(rawMetadata.key),
    // Preserve other fields as-is
  };
}

/**
 * Get available options for dropdowns
 */
export const SONG_FIELD_OPTIONS = {
  genres: Array.from(new Set(Object.values(GENRE_MAPPINGS))).sort(),
  moods: Array.from(new Set(Object.values(MOOD_MAPPINGS))).sort(),
  energyLevels: Array.from(new Set(Object.values(ENERGY_MAPPINGS))).sort(),
  tempos: Array.from(new Set(Object.values(TEMPO_MAPPINGS))).sort(),
  vocalTypes: Array.from(new Set(Object.values(VOCAL_TYPE_MAPPINGS))).sort(),
  ageRatings: Array.from(
    new Set(Object.values(AGE_APPROPRIATENESS_MAPPINGS))
  ).sort(),
};

export default {
  normalizeGenre,
  normalizeMood,
  normalizeEnergy,
  normalizeTempo,
  normalizeVocalType,
  normalizeAgeAppropriateness,
  normalizeBPM,
  normalizeDuration,
  normalizeKey,
  normalizeSongMetadata,
  SONG_FIELD_OPTIONS,
};
